/* eslint-disable complexity */
/* eslint-disable no-param-reassign */

import React, { useCallback } from 'react';
import { ga4Track, modalInteraction } from '@unisporkal/ga4';
import { useTranslations } from '@unisporkal/localization';
import acquisitionTypes from '../../support/acquisitionTypes.ts';
import { EzaPreferenceCookie } from '../../cookies';
import TitleTabs from './titleTabs';
import TitleWithCloseIcon from './TitleWithCloseIcon.tsx';
import { useAcquisitionType } from '../../hooks/stateful/useAcquisitionType';
import { useAsset } from '../../hooks/stateful/useAsset';
import useSelectedIndex from '../../hooks/stateful/useSelectedIndex.ts';
import useSelectedLimitedUseProduct from '../../hooks/stateful/useSelectedLimitedUseProduct';
import useOnLoad from '../../hooks/queries/useOnLoad';
import useDownloadModifiedAsset from '../../hooks/useDownloadModifiedAsset';
import { useRemoveDownloadData } from '../../shared/api/download/downloadModifiedQueries';
import { getAcquisitionLocationId } from '../../support/tracking.ts';
import { trackClickDownloadOptions } from '../../support/ctaTracking';
import useStatus from '../../hooks/stateful/useStatus';
import statusTypes from '../../support/statusTypes';
import SimpleTitle from './simpleTitle';
import {
  getRfSubscriptionAgreement,
  resolveSubFamilyFromAgreement,
} from '../../shared/rfSubscriptionPredicates.ts';
import { getRfSubscriptionTitleKey } from '../../shared/rfSubscriptionLabels.ts';

const downloadTitle = (asset, ezaCompUser, rfSubscriptionTitle) => {
  if (asset.agreementDetails?.length === 1) {
    switch (asset.agreementDetails[0].Type) {
      case 'EA':
        return ezaCompUser ? 'easy_access_comp_caps' : 'easy_access_caps';
      case 'premium_pack':
        return 'ultrapack_name_caps';
      case 'IP':
        return 'ultrapack_name_caps';
      case 'PA':
        return 'premium_access_caps';
      case 'ED':
        return 'editorial_subscription_caps';
      case 'RF':
        return rfSubscriptionTitle;
      default:
        return null;
    }
  } else {
    if (asset.isImage()) {
      return 'download_image_caps';
    }
    return 'download_video_caps';
  }
};

const AcquisitionTitle = () => {
  const {
    acquisitionType,
    acquisitionViewStack,
    availableTypes,
    popAcquisitionView,
    setAcquisitionType,
  } = useAcquisitionType();
  const { setSelectedLimitedUseProduct } = useSelectedLimitedUseProduct();
  const t = useTranslations();
  const { genId, index, downloadAgreements } = useDownloadModifiedAsset();
  const removeDownloadData = useRemoveDownloadData();
  const { setStatus } = useStatus();

  const onDownloadOptionsClick = useCallback(() => {
    trackClickDownloadOptions();
    // Clear the cached completed-download state so the size view starts fresh:
    // a subsequent download (e.g. of a different size) re-runs the request
    // instead of short-circuiting on the already-complete one. The completed-
    // view bounce itself is now prevented at its source in ModifiedDownloadButton.
    removeDownloadData({ genRequestId: genId, index });
    // Reset the lingering DOWNLOADING status set during the prior download so
    // the size view's CTA renders its normal idle label instead of a stuck
    // spinner/"Downloading" copy when the user returns via this back link.
    setStatus(statusTypes.IN_PROGRESS);
    popAcquisitionView();
  }, [removeDownloadData, genId, index, setStatus, popAcquisitionView]);

  const { asset } = useAsset();
  const { setSelectedOptionIndex } = useSelectedIndex();
  const { response: { config: { ezaCompUser = false } = {} } = {} } =
    useOnLoad();
  // Single Seat Subscription header for the modified (gen) download drawer:
  // the SKU-derived sub-family lives on the RF download agreement, not on
  // asset.agreementDetails, so it is resolved here and passed into downloadTitle.
  const rfAgreement = getRfSubscriptionAgreement(downloadAgreements);
  const rfSubFamily = rfAgreement
    ? resolveSubFamilyFromAgreement(rfAgreement)
    : undefined;
  const rfSubscriptionTitle =
    genId && rfSubFamily ? getRfSubscriptionTitleKey(rfSubFamily) : null;
  const downloadAcquisitionTitle = downloadTitle(
    asset,
    ezaCompUser,
    rfSubscriptionTitle
  );
  const ezaPreferenceCookie = EzaPreferenceCookie.find();
  const acquisitionTypesWithoutCloseIcons = [
    acquisitionTypes.MODIFIED_DOWNLOAD,
    acquisitionTypes.MODIFIED_LICENSE,
  ];

  const titleClick = useCallback(
    (acqType) => {
      if (acquisitionType !== acqType.type) {
        setAcquisitionType(acqType.type);
        // When selecting a new acquisitionType the selectedOption will be reset to 0
        setSelectedOptionIndex(0);
        if (acqType === acquisitionTypes.DOWNLOAD) {
          ezaPreferenceCookie.value = 'true';
        } else if (
          acqType === acquisitionTypes.LICENSE &&
          asset.isDownloadable()
        ) {
          // we have selected the License buy card even though EZA/Download is available.
          ezaPreferenceCookie.value = 'false';
        }
      }
    },
    [acquisitionType, setAcquisitionType, ezaPreferenceCookie, asset]
  );

  const closeCurrentAcquisitionView = useCallback(() => {
    popAcquisitionView();

    const locationId = getAcquisitionLocationId(acquisitionType);

    let ga4Event;
    if (acquisitionType === acquisitionTypes.ULTRA_PACK_UPSELL) {
      ga4Event = modalInteraction({
        event: 'close_modal',
        modal_content: 'ultrapack_options',
        modal_type: 'selectors_cta_modal',
        ui_element_location: 'adp_buy_card_ultrapack_modal',
        cta_text: 'x',
        item_id: asset.id,
      });
    } else {
      ga4Event = modalInteraction({
        event: 'close_modal',
        modal_content: locationId,
        modal_type: 'detailed_modal_with_cta',
        ui_element_location: 'modal_x',
        item_id: asset.id,
        modal_selection: 'asset_acquisition_title',
      });
    }
    ga4Track(ga4Event);

    switch (acquisitionType) {
      case acquisitionTypes.LIMITED_USE_PRODUCTS:
        setSelectedLimitedUseProduct(null);
        break;
      default:
        break;
    }
  }, [popAcquisitionView, acquisitionType]);

  // The cart-confirmation view reuses the pre-add tabs/stack (setAcquisitionType
  // replaces the stack top in place), so it must resolve to its single title
  // before the tabs early-return below, otherwise it renders the stale two-tab
  // header (e.g. Ultra pack + Purchase a license) with neither tab highlighted.
  if (acquisitionType === acquisitionTypes.MODIFIED_CART_CONFIRMATION) {
    return <SimpleTitle title={t('purchase_license_caps')} />;
  }

  if (
    acquisitionViewStack.length <= 1 ||
    acquisitionTypesWithoutCloseIcons.includes(acquisitionType)
  ) {
    return (
      <TitleTabs
        availableAcquisitionTypes={availableTypes}
        acquisitionType={acquisitionType}
        onTabClick={titleClick}
        downloadAcquisitionTitle={downloadAcquisitionTitle}
      />
    );
  }

  switch (acquisitionType) {
    case acquisitionTypes.MODIFIED_LICENSE_REDOWNLOAD:
      return <SimpleTitle title={t('download_image_caps')} />;
    case acquisitionTypes.MODIFIED_LIMITED_USE_PRODUCTS:
      return <SimpleTitle title={t('limited_use_title_caps')} />;
    case acquisitionTypes.MODIFIED_DOWNLOAD_COMPLETED:
      return (
        <SimpleTitle
          title={t('download_completed')}
          onBackLinkClick={onDownloadOptionsClick}
        />
      );
    case acquisitionTypes.MODIFIED_DOWNLOAD_IN_PROGRESS:
      return null;
    default:
      break;
  }

  return (
    <TitleWithCloseIcon
      closeCurrentAcquisitionView={closeCurrentAcquisitionView}
      acquisitionType={acquisitionType}
      availableAcquisitionTypes={availableTypes}
      downloadAcquisitionTitle={downloadAcquisitionTitle}
    />
  );
};

export default React.memo(AcquisitionTitle);
